import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRinging, ShieldCheck, ShieldWarning, QrCode } from "@phosphor-icons/react";
import api, { formatApiError, API_BASE } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function SettingsPage() {
  const { user, refreshMe } = useAuth();
  const [setupQr, setSetupQr] = useState(null);
  const [setupSecret, setSetupSecret] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSetupQr(data.qr_code);
      setSetupSecret(data.secret);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const enable = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/mfa/enable", { code });
      toast.success("MFA enabled");
      setSetupQr(null); setSetupSecret(null); setCode("");
      await refreshMe();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const disable = async () => {
    try {
      await api.post("/auth/mfa/disable");
      toast.success("MFA disabled");
      await refreshMe();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-4xl" data-testid="settings-page">
      <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">MODULE / 05 · SETTINGS</div>
      <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Settings.</h1>

      {/* Profile */}
      <section className="border border-zinc-950 mt-8">
        <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase">profile.info</div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="label-sm text-zinc-500">Full name</div>
            <div className="font-display font-bold text-xl mt-1" data-testid="settings-user-name">{user?.name}</div>
          </div>
          <div>
            <div className="label-sm text-zinc-500">Email</div>
            <div className="font-mono text-sm mt-1" data-testid="settings-user-email">{user?.email}</div>
          </div>
          <div>
            <div className="label-sm text-zinc-500">Role</div>
            <div className="font-mono text-sm mt-1 uppercase">{user?.role}</div>
          </div>
          <div>
            <div className="label-sm text-zinc-500">Account created</div>
            <div className="font-mono text-sm mt-1">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</div>
          </div>
        </div>
      </section>

      {/* MFA */}
      <section className="border border-zinc-950 mt-6">
        <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center justify-between">
          <span>security.mfa</span>
          <span className="flex items-center gap-1.5">
            {user?.mfa_enabled ? <ShieldCheck size={12} weight="fill" className="text-emerald-400" /> : <ShieldWarning size={12} className="text-amber-400" />}
            <span>{user?.mfa_enabled ? "ENABLED" : "DISABLED"}</span>
          </span>
        </div>
        <div className="p-6" data-testid="mfa-section">
          {!user?.mfa_enabled && !setupQr && (
            <div>
              <p className="text-sm text-zinc-600 max-w-xl mb-4">
                Add a second layer of security. Once enabled, sign-in will require a code from your authenticator app or sent to your email.
              </p>
              <button onClick={startSetup} disabled={busy} className="bg-zinc-950 text-white text-xs font-semibold px-5 py-2.5 hover:bg-[#FF3E00] disabled:opacity-50 flex items-center gap-2" data-testid="mfa-setup-btn">
                <QrCode size={14} /> {busy ? "GENERATING…" : "SETUP TOTP MFA"}
              </button>
            </div>
          )}

          {setupQr && (
            <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 items-start">
              <div className="border border-zinc-950 p-3 bg-white">
                <img src={setupQr} alt="QR" className="w-full" data-testid="mfa-qr-img" />
              </div>
              <div>
                <div className="label-sm text-zinc-500">Step 1 · Scan QR</div>
                <p className="text-sm text-zinc-600 mt-1">
                  Open Google Authenticator, Authy, or 1Password. Scan the QR code.
                </p>
                <div className="label-sm text-zinc-500 mt-4">Manual secret</div>
                <code className="block font-mono text-xs bg-zinc-100 px-3 py-2 mt-1 break-all" data-testid="mfa-secret-text">{setupSecret}</code>

                <form onSubmit={enable} className="mt-5">
                  <div className="label-sm text-zinc-500">Step 2 · Enter the 6-digit code</div>
                  <div className="flex gap-2 mt-2">
                    <input
                      data-testid="mfa-setup-code-input"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      required
                      className="border border-zinc-300 focus:border-zinc-950 px-3 py-2 tnum text-lg w-40 outline-none transition-colors"
                      placeholder="••••••"
                    />
                    <button type="submit" disabled={code.length < 6} className="bg-zinc-950 text-white text-xs font-semibold px-5 hover:bg-[#FF3E00] disabled:opacity-50" data-testid="mfa-enable-btn">
                      ACTIVATE MFA
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {user?.mfa_enabled && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck size={20} weight="fill" className="text-emerald-600" />
                <span className="font-semibold">MFA is active on this account.</span>
              </div>
              <p className="text-sm text-zinc-600 mb-4">Both TOTP and Email OTP are available for sign-in challenges.</p>
              <button onClick={disable} className="border border-[#D0021B] text-[#D0021B] text-xs font-semibold px-5 py-2.5 hover:bg-[#D0021B] hover:text-white transition-colors" data-testid="mfa-disable-btn">
                DISABLE MFA
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="border border-zinc-200 mt-6 p-6 bg-zinc-50">
        <div className="label-sm text-zinc-500">About SmartERP</div>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          SmartERP Enterprise Suite v1.0 · Cloud-native ERP with Finance, HR, and Supply Chain modules.
          Built API-first with JWT authentication, MFA (TOTP + Email OTP), brute-force protection,
          and an AI Copilot powered by Claude Sonnet 4.5.
        </p>
      </section>

      {/* GDPR export */}
      <section className="border border-zinc-950 mt-6">
        <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase">privacy.gdpr</div>
        <div className="p-6">
          <p className="text-sm text-zinc-600 max-w-xl mb-4">
            Download a JSON bundle containing all your personal data plus your audit trail entries.
            Admins also get the full tenant dataset. Right to data portability — GDPR Art. 20.
          </p>
          <button
            onClick={async () => {
              const API = `${API_BASE}/api`;
              const res = await fetch(`${API}/gdpr/export`, { credentials: "include" });
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `smarterp_my_data.json`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            }}
            className="bg-zinc-950 text-white text-xs font-semibold px-5 py-2.5 hover:bg-[#FF3E00] transition-colors"
            data-testid="gdpr-export-btn"
          >
            DOWNLOAD MY DATA
          </button>
        </div>
      </section>

      <section className="border border-zinc-950 mt-6" data-testid="notifications-section">
        <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center gap-2">
          <BellRinging size={12} /> notifications
        </div>
        <div className="p-6 flex items-center justify-between gap-6">
          <div className="flex-1">
            <div className="font-semibold text-sm">Workflow approval emails</div>
            <div className="text-xs text-zinc-500 mt-1">Email me when I&apos;m an approver on a new workflow run, and when my submitted requests get approved or rejected.</div>
          </div>
          <button
            onClick={async () => {
              const next = !(user?.notify_workflows !== false); // default true; flip current effective value
              const newVal = !next;  // toggle
              try {
                await api.patch("/me/preferences", { notify_workflows: newVal });
                toast.success(newVal ? "Notifications enabled" : "Notifications muted");
                refreshMe();
              } catch (err) {
                toast.error(formatApiError(err.response?.data?.detail));
              }
            }}
            className={`relative w-12 h-6 transition-colors ${user?.notify_workflows === false ? "bg-zinc-300" : "bg-emerald-600"}`}
            data-testid="notify-workflows-toggle"
            aria-label="Toggle workflow notifications"
          >
            <span className={`absolute top-0.5 w-5 h-5 bg-white transition-all ${user?.notify_workflows === false ? "left-0.5" : "left-6"}`} />
          </button>
        </div>
      </section>
    </div>
  );
}
